'use strict';
/* ==========================================================================
   core-local/narration.js — "Who's Telling This?": the narrator model (DESIGN.md §4). Pure: no DOM, no engine globals
   (only core/bank.js's seeded rng for the draws). Runs in node (module.exports: tools/narrationcheck.js,
   tools/contentcheck.js) and in the page (window.Narration). Registers itself with the question bank as `narration`
   when core/bank.js is loaded first (lesson.json scripts order).

   The honesty rule for this lesson: EVERY point of view and EVERY reliability verdict is computed HERE from tagged
   passages, never trusted from a content file. The tags describe what is on the page (whose thoughts a sentence reports
   as fact, what the narrator claims, which sentence undercuts a claim); the answers are derived from them.

   ---- a passage (content/bank.cases.json, content/bank.pov.json, SCRIPT below) ----
   { sentences: [{ text,
                   speech?: true      (dialogue / a quoted book line: left out of point-of-view detection; "…" spans
                                       inside any sentence are left out too),
                   thoughts?: [who]   (whose inner thoughts / feelings the narration reports AS FACT),
                   claim?: id         (a claim the narrator makes),
                   evidence?: {against: claimId, clue} | [ … ]   (this sentence undercuts that claim; clue = a CLUES id),
                   limit?: text       (the narrator admits what it can't know: a head it can't enter, a thing it didn't see) }],
     teller?: who        the character who is the "I" (first-person passages; the protagonist "you" is 'you'),
     cast?: {who: name}  display names (eye icons, generated remediation),
     why?: kind          the reason a narrator is unreliable: 'dishonest' | 'missing-info' | 'self-deceiving' (Florida's
                         N1 list minus "mental illness", Correction 4); present iff the passage is unreliable }

   ---- the model ----
   Narration.personOf(sentence) → 1 | 2 | 3 | null  from the narration's pronouns (dialogue and "…" removed):
        I/me/my/we/us/our… → 1; else you/your… → 2; else he/she/they… → 3; else null (no pronoun: inherits)
   Narration.pov(p) → {person, degree, id, label, heads: [who], shift: null | {at, kind, to: pov}}
        person over the passage (or, with a shift, over the part before it): any 1 → 1, else any 2 → 2, else 3;
        degree: first person is ALWAYS 'limited' (Florida's term "first-person limited"); otherwise the number of
        distinct heads in `thoughts`: 1 → 'limited', ≥ 2 → 'omniscient'. id: 'first-limited' | 'second' |
        'third-limited' | 'third-omniscient'; label: Florida's wording (POVS below).
   Narration.povRange(p, a, b)   the same over sentences [a, b)
   Narration.shift(p) → the sentence index where the person or degree changes, or -1:
        person: an "I" (or "you") appears in narration after third person; or, in a first/second-person passage, the
        narration reports a head that is not the teller's (it has left the "I");
        degree (third person): a second head appears after a stretch of ≥ SHIFT_STRETCH sentences in one head (with ≥ 2
        of them reporting that head's thoughts). An omniscient passage visits its second head sooner than that.
   Narration.shiftInfo(p) → {at, kind: 'person' | 'degree', from: pov, to: pov} | null
   Narration.reliability(p) → {reliable, kind, clues: [sentence indices], evidence: [{i, clue, against}], claims: [i]}
        unreliable iff some claim has evidence against it; kind = p.why (validated against the clues: KIND_RULES).
   Narration.limits(p) → [sentence indices] (what a limited narrator admits it can't know)
   Narration.narrator(p) → the teller for first person, else 'outside' (a narrator outside the story). NEVER 'author'.
   Narration.validate(p) → [problems]  (a passage the model can't read honestly: a check fails on any)
   Narration.CLUES[id] {id, name (the evidence tag's text), colour, ask (the kid checklist question), cite}
   Narration.CLUE_IDS, Narration.KINDS[id] {id, label (the trust option), short, florida}, Narration.POVS[id] {id, person,
   degree, label, short}, Narration.name(id) / colour(id) (clue, POV or verdict id → its tag text / highlighter)

   ---- the practice pools (draws through core/bank.js's seeded rng; tags are COMPUTED, never read from content) ----
   Narration.tagsOf(item, pool) → cases: ['reliable'] | [kind] (+ 'unreliable'); pov: [pov id] (+ 'shift'); + 'pd' when
        the passage is a public-domain excerpt (source.author)
   Narration.caseInstance(item, r, slots) / Narration.povInstance(item, r, slots) (see the draw functions)
   Narration.drawCases(json, {seed, avoid}) → {seed, items: [case]}   draw.n items: draw.require tags first (a tag
        repeated = that many), draw.max caps ({pd: 1}), fresh items first, served easiest first (level).
        case: {id, key, level, title, source, sentences: [{text, speech}], cast, verdict: reliability(),
          trust: {prompt, options: [{id, label}], answerIndex, why: [per option]},
          tap: {prompt, answers: [i], clueOf: {i: clueId}, why(i) → text},  explain, item}
   Narration.drawPov(json, {seed, avoid}) → {seed, items: [pov]}
        pov: {id, key, level, title, source, sentences, cast, pov: pov(), heads: [{id, name}], shift: shiftInfo(),
          name: {prompt, options: [{id, label}], answerIndex, why: [..]},                    (Part A: which point of view)
          ask: {type: 'choice' | 'tap', kind, prompt, options?: [{label, who}], answerIndex?, why?: [..],
                answers?: [i], whyTap?(i)},                                                (Part B)
          explain, item}
   Narration.attempt(step, json, {fresh}) → {seed, attempt, resumed, items}   saved like Bank.attempt in
        Game.progress.bank[step] ({pool, seed, attempt, ids, prev, open}); an open attempt resumes; ?seed= or
        window.__bankSeed forces one.   Narration.finish(step) = Bank.finish(step).
   Narration.SCRIPT {volcano, bea, cameraFirst, cameraThird, q3, poe}: the video's passages (tools/narrationcheck.js
        checks each against content/script.json).
   ========================================================================== */
(function (root) {
  const B = () => root.Bank || (typeof require === 'function' ? tryRequireBank() : null);
  function tryRequireBank() { try { return require('../../../shared/engine/v2/core/bank.js'); } catch (e) { return null; } }

  /* ---------------- vocabulary (docs/topics/facts-narrator.md) ---------------- */
  // the clue checklist: facts §5 "Suggested kid checklist" (our synthesis of N21–N24); tag text = DESIGN.md §2
  const CL = (id, name, colour, ask, cite) => Object.freeze({ id, name, colour, ask, cite });
  const CLUES = Object.freeze({
    'says-vs-does': CL('says-vs-does', 'SAYS vs DOES', '#ff9d33', 'Do the narrator\'s actions match their words?', 'facts §5 (1), N23 Smith 6, N21 (d)'),
    'others-react': CL('others-react', 'OTHERS REACT', '#52c8ff', 'Does everyone else react differently?', 'facts §5 (2), N15, N21 (c)'),
    'contradicts': CL('contradicts', 'CONTRADICTS', '#ff4d4d', 'Does the narrator contradict themself?', 'facts §5 (3), N13, N21 (d)'),
    'something-to-gain': CL('something-to-gain', 'SOMETHING TO GAIN', '#72f06e', 'Would the story get the narrator out of trouble?', 'facts §5 (4), N23 Smith 1, N1 "purposeful dishonesty"'),
    'missing-info': CL('missing-info', 'MISSING INFO', '#b98aff', 'Is the narrator too young, new or uninformed to understand?', 'facts §5 (5), N23 Smith 2-3, N1 "lack of information"'),
    'cant-be-true': CL('cant-be-true', 'CAN\'T BE TRUE', '#fff04a', 'Does it clash with what you know is possible?', 'facts §5 (6), N22 (extratextual)'),
    'big-feelings': CL('big-feelings', 'BIG FEELINGS', '#ff6fbf', 'Is the narrator too upset or excited to see clearly?', 'facts §5 (7), N23 Smith 5'),
  });
  const CLUE_IDS = Object.freeze(Object.keys(CLUES));

  // Florida's reasons (N1 Clarification 1), minus "mental illness" (never a reveal here: Correction 4)
  const KINDS = Object.freeze({
    dishonest: Object.freeze({ id: 'dishonest', label: 'Unreliable: lying to get something', short: 'lying on purpose', florida: 'purposeful dishonesty' }),
    'missing-info': Object.freeze({ id: 'missing-info', label: 'Unreliable: missing information', short: 'honest, but missing information', florida: 'a lack of information or background knowledge' }),
    'self-deceiving': Object.freeze({ id: 'self-deceiving', label: 'Unreliable: fooling themself', short: 'fooling themself', florida: 'self-deception' }),
  });
  const KIND_IDS = Object.freeze(Object.keys(KINDS));
  const RELIABLE = Object.freeze({ id: 'reliable', label: 'Reliable: the details add up', short: 'reliable' });

  // Florida's grade-7 terms exactly (N1 Clarification 2) + second person from the glossary (N5); no "objective" (Correction 2)
  const PV = (id, person, degree, label, short) => Object.freeze({ id, person, degree, label, short });
  const POVS = Object.freeze({
    'first-limited': PV('first-limited', 1, 'limited', 'First-person limited', 'I, one head'),
    'second': PV('second', 2, 'limited', 'Second-person', 'you'),
    'third-limited': PV('third-limited', 3, 'limited', 'Third-person limited', 'he or she, one head'),
    'third-omniscient': PV('third-omniscient', 3, 'omniscient', 'Third-person omniscient', 'he or she, every head'),
  });
  const POV_IDS = Object.freeze(Object.keys(POVS));
  const POV_COLOUR = { 'first-limited': '#ff9d33', second: '#2fe3c9', 'third-limited': '#52c8ff', 'third-omniscient': '#b98aff' };

  /* which clues each kind needs (a kind is the passage's `why` tag; these rules keep it honest against its clues):
     dishonest       at least one SOMETHING TO GAIN
     missing-info    at least one MISSING INFO or CAN'T BE TRUE, and nothing to gain
     self-deceiving  nothing to gain, no MISSING INFO, and at least one SAYS vs DOES / BIG FEELINGS / OTHERS REACT /
                     CONTRADICTS */
  const KIND_RULES = Object.freeze({
    dishonest: c => c.includes('something-to-gain'),
    'missing-info': c => (c.includes('missing-info') || c.includes('cant-be-true')) && !c.includes('something-to-gain'),
    'self-deceiving': c => !c.includes('something-to-gain') && !c.includes('missing-info') && c.some(x => ['says-vs-does', 'big-feelings', 'others-react', 'contradicts'].includes(x)),
  });

  const nameOf = id => (CLUES[id] ? CLUES[id].name : POVS[id] ? POVS[id].label : KINDS[id] ? KINDS[id].short : id === 'reliable' ? 'RELIABLE' : id === 'unreliable' ? 'UNRELIABLE' : String(id));
  const colourOf = id => (CLUES[id] ? CLUES[id].colour : POV_COLOUR[id] || (id === 'reliable' ? '#72f06e' : id === 'unreliable' ? '#ff4d4d' : '#fff04a'));

  /* ---------------- point of view ---------------- */
  const SHIFT_STRETCH = 3;
  const QUOTES = /"[^"]*"|“[^”]*”/g;
  const FIRST = /\b(?:I|[Mm]e|[Mm]y|[Mm]ine|[Mm]yself|[Ww]e|[Uu]s|[Oo]ur|[Oo]urs|[Oo]urselves)\b/;
  const SECOND = /\b(?:you|your|yours|yourself|yourselves)\b/i;
  const THIRD = /\b(?:he|him|his|himself|she|her|hers|herself|they|them|their|theirs|themselves)\b/i;
  const sentencesOf = p => (p && Array.isArray(p.sentences) ? p.sentences : []);
  const narrationText = s => (!s || s.speech ? '' : String(s.text || '').replace(QUOTES, ' '));
  function personOf(s) {
    const n = narrationText(s);
    if (!n.trim()) return null;
    if (FIRST.test(n)) return 1;
    if (SECOND.test(n)) return 2;
    if (THIRD.test(n)) return 3;
    return null;
  }
  const thoughtsOf = s => (s && !s.speech && Array.isArray(s.thoughts) ? s.thoughts : []);
  const protagonist = (p, person) => (person === 2 ? 'you' : p.teller);

  function povRange(p, a = 0, b = sentencesOf(p).length) {
    const S = sentencesOf(p).slice(a, b), prs = S.map(personOf);
    const person = prs.includes(1) ? 1 : prs.includes(2) ? 2 : 3;
    const heads = [];
    const me = person === 1 || person === 2 ? protagonist(p, person) : null;
    if (me) heads.push(me);                                   // an I (or you) narrator is always in its own head
    S.forEach(s => thoughtsOf(s).forEach(w => { if (!heads.includes(w)) heads.push(w); }));
    const degree = person === 1 ? 'limited' : heads.length >= 2 ? 'omniscient' : 'limited';
    const id = person === 1 ? 'first-limited' : person === 2 ? 'second' : degree === 'omniscient' ? 'third-omniscient' : 'third-limited';
    return { person, degree, id, label: POVS[id].label, heads, range: [a, b] };
  }

  /* the first sentence where the narration's person or degree changes (see the header) */
  function shiftScan(p, from = 0) {
    const S = sentencesOf(p);
    let person = null;
    for (let i = from; i < S.length && person == null; i++) person = personOf(S[i]);
    if (person == null) person = 3;
    const heads = new Set();
    let thoughtLines = 0;
    for (let i = from; i < S.length; i++) {
      const s = S[i], pr = personOf(s), th = thoughtsOf(s);
      if (person === 3) {
        if (pr === 1 || pr === 2) return { at: i, kind: 'person' };
        const fresh = th.filter(w => !heads.has(w));
        if (fresh.length && heads.size === 1 && i - from >= SHIFT_STRETCH && thoughtLines >= 2) return { at: i, kind: 'degree' };
      } else {
        const me = protagonist(p, person);
        if (pr != null && pr !== 3 && pr !== person) return { at: i, kind: 'person' };
        if (th.some(w => w !== me) && pr !== person) return { at: i, kind: 'person' };
      }
      th.forEach(w => heads.add(w));
      if (th.length) thoughtLines++;
    }
    return null;
  }
  const shift = p => { const s = shiftScan(p); return s ? s.at : -1; };
  function shiftInfo(p) {
    const s = shiftScan(p);
    if (!s) return null;
    // a shift in degree (limited → omniscient) makes the passage as a whole omniscient; a shift in person is read on the
    // part after it (Florida's example: "from first-person limited to third-person limited", N1)
    return { at: s.at, kind: s.kind, from: povRange(p, 0, s.at), to: s.kind === 'degree' ? povRange(p) : povRange(p, s.at) };
  }
  function pov(p) {
    const s = shiftInfo(p);
    const v = povRange(p, 0, s ? s.at : sentencesOf(p).length);
    v.shift = s ? { at: s.at, kind: s.kind, to: s.to } : null;
    return v;
  }
  const narrator = p => (pov(p).person === 1 ? p.teller : 'outside');

  /* ---------------- reliability ---------------- */
  const evidenceOf = s => (!s || !s.evidence ? [] : Array.isArray(s.evidence) ? s.evidence : [s.evidence]);
  function reliability(p) {
    const S = sentencesOf(p), claims = {};
    S.forEach((s, i) => { if (s.claim) (claims[s.claim] = claims[s.claim] || []).push(i); });
    const evidence = [];
    S.forEach((s, i) => evidenceOf(s).forEach(e => { if (e && claims[e.against]) evidence.push({ i, clue: e.clue, against: e.against }); }));
    const reliable = evidence.length === 0;
    const clues = [...new Set(evidence.map(e => e.i))].sort((a, b) => a - b);
    return { reliable, kind: reliable ? null : (p.why || null), clues, evidence, claims: Object.values(claims).flat().sort((a, b) => a - b) };
  }
  const limits = p => sentencesOf(p).map((s, i) => (s.limit ? i : -1)).filter(i => i >= 0);

  /* ---------------- validation: a passage the model can't read honestly ---------------- */
  function validate(p) {
    const e = [], S = sentencesOf(p);
    if (!S.length) return ['no sentences'];
    S.forEach((s, i) => {
      if (!s || typeof s.text !== 'string' || !s.text.trim()) e.push(`sentence ${i}: no text`);
      thoughtsOf(s).forEach(w => { if (p.cast && !(w in p.cast) && w !== 'you') e.push(`sentence ${i}: thoughts "${w}" is not in cast`); });
      evidenceOf(s).forEach(ev => {
        if (!ev || !CLUES[ev.clue]) e.push(`sentence ${i}: evidence clue "${ev && ev.clue}" is not a clue id`);
        if (!ev || !S.some(x => x.claim === ev.against)) e.push(`sentence ${i}: evidence against "${ev && ev.against}", which no sentence claims`);
      });
      if (s.speech && Array.isArray(s.thoughts) && s.thoughts.length) e.push(`sentence ${i}: a speech sentence carries thoughts (dialogue is not narration)`);
    });
    const v = pov(p), r = reliability(p), sh = v.shift;
    // first person: the "I" is named, and an I-sentence reports only the I's head
    const segs = sh ? [[0, sh.at], [sh.at, S.length]] : [[0, S.length]];
    for (const [a, b] of segs) {
      const seg = povRange(p, a, b);
      if (seg.person === 1) {
        if (!p.teller) e.push('first person, but no "teller" (who the I is)');
        else S.slice(a, b).forEach((s, k) => { if (thoughtsOf(s).some(w => w !== p.teller)) e.push(`sentence ${a + k}: a first-person sentence reports another head`); });
      }
      if (seg.person === 3 && seg.heads.length === 0) e.push(`sentences ${a}-${b - 1}: third person with no head at all (Florida names no "objective" point of view: Correction 2)`);
    }
    if (sh && shiftScan(p, sh.at)) e.push(`more than one shift (a second at ${shiftScan(p, sh.at).at})`);
    if (r.reliable && p.why) e.push(`why "${p.why}", but no claim has evidence against it`);
    if (!r.reliable) {
      if (!KINDS[p.why]) e.push(`unreliable, but why "${p.why}" is not a kind`);
      else if (!KIND_RULES[p.why](r.evidence.map(x => x.clue))) e.push(`why "${p.why}" doesn't fit its clues (${r.evidence.map(x => x.clue).join(', ')})`);
    }
    return e;
  }

  /* ---------------- pools: computed tags ---------------- */
  const isPd = it => !!(it && it.source && typeof it.source === 'object' && it.source.author);
  function tagsOf(it, pool) {
    const t = [];
    if (pool === 'cases') { const r = reliability(it); if (r.reliable) t.push('reliable'); else t.push('unreliable', r.kind); }
    else { const v = pov(it); t.push(v.id); if (v.shift) t.push('shift'); }
    if (isPd(it)) t.push('pd');
    return t;
  }
  const castName = (it, w) => (w === 'you' ? 'you' : (it.cast && it.cast[w]) || w);
  const poss = n => (n === 'you' ? 'your' : /^the /.test(n) ? n + '\'s' : n + '\'s');
  const list = arr => (arr.length <= 1 ? arr.join('') : arr.length === 2 ? `${arr[0]} and ${arr[1]}` : `${arr.slice(0, -1).join(', ')} and ${arr[arr.length - 1]}`);

  /* ---- Case Files: Part A trust + kind, Part B tap the sentence ---- */
  const TRUST_PROMPT = 'Can you trust this narrator?';
  const TAP_UNRELIABLE = 'Tap the sentence that gives the narrator away.';
  const TAP_RELIABLE = 'Tap the sentence that shows what this narrator CAN\'T know.';
  const DEFAULT_TRAPS = { reliable: ['dishonest', 'self-deceiving'], dishonest: ['reliable', 'self-deceiving'], 'missing-info': ['reliable', 'dishonest'], 'self-deceiving': ['reliable', 'dishonest'] };
  /* Part A's three options: the right verdict + two traps. Unreliable: "reliable" is always a trap (plus a wrong kind);
     reliable: two kinds. item.traps may choose them (the check proves each is wrong). */
  function caseOptions(it) {
    const r = reliability(it), right = r.reliable ? 'reliable' : r.kind;
    let traps = Array.isArray(it.traps) ? it.traps.slice(0, 2) : DEFAULT_TRAPS[right].slice();
    if (!r.reliable && !traps.includes('reliable')) traps = ['reliable', traps.find(x => x !== right && x !== 'reliable')];
    return { right, ids: [right, ...traps] };
  }
  const optLabel = id => (id === 'reliable' ? RELIABLE.label : KINDS[id].label);
  function caseWhyNot(it, id) {
    const w = it.whyNot && it.whyNot[id];
    if (w) return w;
    const r = reliability(it);
    if (id === 'reliable') return `The details don't add up: look at sentence ${r.clues.map(i => i + 1).join(', ')}.`;
    if (r.reliable) return 'Nothing in it undercuts what the narrator says.';
    return `Not quite: this narrator is ${KINDS[r.kind].short}.`;
  }
  function caseTapWhy(it, i) {
    const r = reliability(it), S = sentencesOf(it);
    if (r.reliable) return limits(it).includes(i) ? '' : 'The narrator can see or know that one. Look for what they admit they can\'t know.';
    if (r.clues.includes(i)) return '';
    if (S[i] && S[i].claim) return 'That\'s the claim itself. Which sentence undercuts it?';
    return 'That sentence doesn\'t give the narrator away. Look for what doesn\'t add up.';
  }
  function caseInstance(it, r, slots) {
    const rel = reliability(it), { right, ids } = caseOptions(it);
    const order = place(r, ids, slots(ids.length));
    const clueOf = {};
    rel.evidence.forEach(e => { if (clueOf[e.i] == null) clueOf[e.i] = e.clue; });
    return { id: it.id, key: it.id, level: it.level || 1, title: it.title || '', source: it.source || 'ours', tags: tagsOf(it, 'cases'),
      misconception: it.misconception || null, sentences: sentencesOf(it).map(s => ({ text: s.text, speech: !!s.speech })), cast: it.cast || {},
      verdict: rel, pov: pov(it),
      trust: { prompt: TRUST_PROMPT, options: order.map(id => ({ id, label: optLabel(id) })), answerIndex: order.indexOf(right), why: order.map(id => (id === right ? '' : caseWhyNot(it, id))) },
      tap: { prompt: rel.reliable ? TAP_RELIABLE : TAP_UNRELIABLE, answers: rel.reliable ? limits(it) : rel.clues.slice(), clueOf, why: i => caseTapWhy(it, i) },
      explain: it.explain || '', item: it };
  }

  /* ---- Whose Head?: Part A the point of view, Part B influence / limit / shift ---- */
  const NAME_PROMPT = 'Which point of view?';
  const NAME_PROMPT_SHIFT = 'Which point of view does it START in?';
  const SHIFT_PROMPT = 'Tap the sentence where the point of view shifts.';
  const DEFAULT_POV_TRAPS = { 'first-limited': ['third-limited', 'third-omniscient'], 'third-limited': ['third-omniscient', 'first-limited'], 'third-omniscient': ['third-limited', 'first-limited'], second: ['first-limited', 'third-limited'] };
  function povOptions(it) {
    const v = pov(it), traps = Array.isArray(it.traps) ? it.traps.slice(0, 2) : DEFAULT_POV_TRAPS[v.id].slice();
    return { right: v.id, ids: [v.id, ...traps] };
  }
  /* why a wrong point of view is wrong: generated from the passage's own tags (item.whyNot[povId] overrides) */
  function povWhyNot(it, id) {
    const w = it.whyNot && it.whyNot[id];
    if (w) return w;
    const v = pov(it), heads = v.heads.map(h => castName(it, h));
    const P = POVS[id];
    if (P.person !== v.person) {
      if (v.person === 1) return 'The narrator says I and tells it from inside the story: first person.';
      if (v.person === 2) return 'The main character is you, told from outside: second person.';
      if (P.person === 1) return 'There\'s no I telling it, outside the dialogue.';
      if (P.person === 2) return 'Nobody is called you in the narration.';
      return 'The narration says he or she: third person.';
    }
    if (P.degree === 'omniscient') return `It stays in one head: ${poss(heads[0] || 'one character')}.`;
    return `It goes inside more than one head: ${list(heads)}.`;
  }
  /* Part B, computed where the model can answer it:
     cant-know    options {label, who}: the right one is the only option about a head the narrator does NOT enter
                  (who = a cast id not in heads and not the teller); who null = something the narrator saw
     heads        options {label, who: [ids]}: the right one names exactly the heads
     narrator     options {label, who}: the right one is narrator(p) (the teller, or 'outside'); 'author' is never right
     shift        tap: the right sentence is shift(p)
     perspective  options + correct (the attitude: a reading, reviewed by the designer); an option that is a point-of-view
                  label is never right (Correction 3)
     influence    options + correct (why the author chose it: a reading, reviewed by the designer) */
  function askAnswer(it) {
    const a = it.ask || {}, v = pov(it), opts = a.options || [];
    const heads = new Set(v.heads.concat(v.person === 1 ? [it.teller] : []));
    switch (a.type) {
      case 'cant-know': { const k = opts.map((o, i) => (o.who && o.who !== 'you' && !heads.has(o.who) && o.who !== it.teller ? i : -1)).filter(i => i >= 0); return k.length === 1 ? k[0] : -1; }
      case 'heads': { const want = [...v.heads].sort().join(); const k = opts.map((o, i) => (Array.isArray(o.who) && [...o.who].sort().join() === want ? i : -1)).filter(i => i >= 0); return k.length === 1 ? k[0] : -1; }
      case 'narrator': { const n = narrator(it); const k = opts.map((o, i) => (o.who === n && n !== 'author' ? i : -1)).filter(i => i >= 0); return k.length === 1 ? k[0] : -1; }
      case 'perspective': case 'influence': {
        const c = a.correct;
        if (!Number.isInteger(c) || c < 0 || c >= opts.length) return -1;
        if (a.type === 'perspective' && POV_IDS.some(id => POVS[id].label.toLowerCase() === String(opts[c].label).toLowerCase())) return -1;
        return c;
      }
      case 'shift': return shift(it);
      default: return -1;
    }
  }
  function povInstance(it, r, slots) {
    const v = pov(it), si = shiftInfo(it), { right, ids } = povOptions(it);
    const order = place(r, ids, slots(ids.length));
    const a = it.ask || {};
    let ask;
    if (a.type === 'shift') {
      const at = shift(it);
      ask = { type: 'tap', kind: 'shift', prompt: a.prompt || SHIFT_PROMPT, answers: [at],
        whyTap: i => (i === at ? '' : i < at ? `Still ${povRange(it, 0, at).label.toLowerCase()} there.` : `By then it has already shifted. Look earlier.`) };
    } else {
      const opts = a.options || [], k = askAnswer(it);
      const oi = place(r, [k, ...opts.map((o, i) => i).filter(i => i !== k)], slots(opts.length));
      ask = { type: 'choice', kind: a.type, prompt: a.prompt, options: oi.map(i => ({ label: opts[i].label, who: opts[i].who == null ? null : opts[i].who })),
        answerIndex: oi.indexOf(k), why: oi.map(i => (i === k ? '' : (a.whyNot && a.whyNot[String(i)]) || '')) };
    }
    return { id: it.id, key: it.id, level: it.level || 1, title: it.title || '', source: it.source || 'ours', tags: tagsOf(it, 'pov'),
      misconception: it.misconception || null, sentences: sentencesOf(it).map(s => ({ text: s.text, speech: !!s.speech })), cast: it.cast || {},
      pov: v, heads: v.heads.map(h => ({ id: h, name: castName(it, h) })), shift: si,
      name: { prompt: si ? NAME_PROMPT_SHIFT : NAME_PROMPT, options: order.map(id => ({ id, label: POVS[id].label })), answerIndex: order.indexOf(right),
        why: order.map(id => (id === right ? '' : povWhyNot(it, id))) },
      ask, explain: it.explain || '', item: it };
  }

  /* ---------------- drawing (through the shared bank's seeded rng) ---------------- */
  /* pick draw.n items: draw.require tags first (a tag listed k times needs k items), draw.max caps a tag, fresh (not in
     avoid) first; then easiest first (level) */
  function pick(json, r, avoid, pool) {
    const d = json.draw || {}, items = json.items || [], n = Math.min(d.n || items.length, items.length);
    const avoidSet = new Set(d.avoidRepeat === false ? [] : avoid || []), max = d.max || {};
    const T = new Map(items.map(it => [it.id, tagsOf(it, pool)]));
    const chosen = [], taken = new Set();
    const count = tag => chosen.filter(it => T.get(it.id).includes(tag)).length;
    const capped = it => Object.entries(max).some(([tag, m]) => T.get(it.id).includes(tag) && count(tag) >= m);
    const choose = cands => {
      const ok = cands.filter(it => !taken.has(it.id) && !capped(it)), fresh = ok.filter(it => !avoidSet.has(it.id)), any = fresh.length ? fresh : ok;
      if (!any.length) return false;
      const it = r.pick(any); chosen.push(it); taken.add(it.id); return true;
    };
    const need = {};
    (d.require || []).forEach(tag => { need[tag] = (need[tag] || 0) + 1; });
    for (const tag of r.shuffle(Object.keys(need))) while (chosen.length < n && count(tag) < need[tag] && choose(items.filter(it => T.get(it.id).includes(tag))));
    while (chosen.length < n && choose(items));
    return chosen.map((it, k) => ({ it, k })).sort((a, b) => (a.it.level || 1) - (b.it.level || 1) || a.k - b.k).map(x => x.it);
  }
  /* least-used slot first, ties at random (the right answers spread over the slots, like Bank.draw) */
  function spreader(r) {
    const used = [0, 0, 0, 0, 0];
    return n => { const lo = Math.min(...used.slice(0, n)), c = []; for (let i = 0; i < n; i++) if (used[i] === lo) c.push(i); const s = r.pick(c); used[s]++; return s; };
  }
  /* list[0] (the right one) placed at `slot`, the others shuffled */
  const place = (r, list, slot) => { const rest = r.shuffle(list.slice(1)); return rest.slice(0, slot).concat([list[0]], rest.slice(slot)); };

  function drawCases(json, { seed, avoid = [] } = {}) {
    const Bk = B(), s = seed == null ? Bk.newSeed() : seed, r = Bk.rng(s), slots = spreader(r);
    return { seed: s, items: pick(json, r, avoid, 'cases').map(it => caseInstance(it, r, slots)) };
  }
  function drawPov(json, { seed, avoid = [] } = {}) {
    const Bk = B(), s = seed == null ? Bk.newSeed() : seed, r = Bk.rng(s), slots = spreader(r);
    return { seed: s, items: pick(json, r, avoid, 'pov').map(it => povInstance(it, r, slots)) };
  }

  /* attempts, saved like Bank.attempt (Game.progress.bank[step]) */
  function attempt(step, json, { fresh = false } = {}) {
    const Bk = B(), G = root.Game, P = G && G.progress;
    const S = P && P.bank && typeof P.bank === 'object' ? P.bank : (Narration._memory || (Narration._memory = {}));
    const st = S[step], forced = Bk.seedOverride(), pool = json.pool;
    let seed, prev, no, resumed = false;
    if (!fresh && st && st.open && st.pool === pool && (forced == null || String(forced) === String(st.seed))) { seed = st.seed; prev = st.prev || []; no = st.attempt || 1; resumed = true; }
    else { seed = forced != null ? forced : Bk.newSeed(); prev = (st && st.ids) || []; no = ((st && st.attempt) || 0) + 1; }
    const drawn = pool === 'pov' ? drawPov(json, { seed, avoid: prev }) : drawCases(json, { seed, avoid: prev });
    S[step] = { pool, seed, attempt: no, ids: drawn.items.map(x => x.id), prev, open: true };
    if (P && P.save) P.save();
    return { seed, attempt: no, resumed, items: drawn.items };
  }
  const finish = step => { const Bk = B(); if (Bk && Bk.finish) Bk.finish(step); };

  /* ---------------- the video's passages (content/script.json; tools/narrationcheck.js checks the text) ---------------- */
  const SCRIPT = Object.freeze({
    // s2 "My Volcano" (written for this lesson): the storyboard's five numbered sentences; clues at 3, 4, 5
    volcano: { title: 'My Volcano', source: 'ours', teller: 'marcus', cast: { marcus: 'Marcus', mom: 'Mom', pepper: 'Pepper' }, why: 'dishonest',
      sentences: [
        { text: 'My volcano was perfect. I followed every step.', thoughts: ['marcus'] },
        { text: 'Then Pepper, our dog, jumped up and knocked it off the table.', claim: 'pepper-did-it' },
        { text: 'Mom looked at the empty vinegar bottle, and then at me, for a long time.', evidence: { against: 'pepper-did-it', clue: 'others-react' } },
        { text: 'Pepper had been asleep upstairs all afternoon, but that\'s not important.', evidence: { against: 'pepper-did-it', clue: 'contradicts' } },
        { text: 'Anyway, I definitely deserve an extension.', evidence: { against: 'pepper-did-it', clue: 'something-to-gain' } },
      ] },
    // s4 Bea's version (written for this lesson; Bea is five): honest, missing information
    bea: { title: 'Bea\'s Version', source: 'ours', teller: 'bea', cast: { bea: 'Bea', marcus: 'Marcus' }, why: 'missing-info',
      sentences: [
        { text: 'I saw Marcus do a magic trick in the kitchen.', claim: 'magic', evidence: { against: 'magic', clue: 'missing-info' } },
        { text: 'He made the whole bottle of vinegar disappear, and a foam dragon came out!', evidence: { against: 'magic', clue: 'missing-info' } },
      ] },
    // s3 the three cameras
    cameraFirst: { source: 'ours', teller: 'marcus', cast: { marcus: 'Marcus' },
      sentences: [{ text: 'I stared at the puddle.' }, { text: 'I hoped nobody had seen.', thoughts: ['marcus'] }] },
    cameraThird: { source: 'ours', cast: { marcus: 'Marcus' },
      sentences: [{ text: 'Marcus stared at the puddle.' }, { text: 'He hoped nobody had seen.', thoughts: ['marcus'] }] },
    q3: { source: 'ours', cast: { marcus: 'Marcus', pepper: 'Pepper', mom: 'Mom' },
      sentences: [
        { text: 'Marcus hoped nobody had seen.', thoughts: ['marcus'] },
        { text: 'Upstairs, Pepper dreamed about squirrels.', thoughts: ['pepper'] },
        { text: 'In the kitchen, Mom already knew exactly what had happened.', thoughts: ['mom'] },
      ] },
    // s1 Poe, "The Tell-Tale Heart", the 1850 (Works) text (N13, Correction 6)
    poe: { title: 'The Tell-Tale Heart', source: { author: 'Edgar Allan Poe', work: 'The Tell-Tale Heart', year: 1850, cite: 'N13' }, teller: 'narrator', cast: { narrator: 'the narrator' },
      sentences: [{ text: 'TRUE! — nervous — very, very dreadfully nervous I had been and am; but why will you say that I am mad?' }] },
  });

  const Narration = {
    CLUES, CLUE_IDS, KINDS, KIND_IDS, KIND_RULES, RELIABLE, POVS, POV_IDS, SHIFT_STRETCH, SCRIPT,
    name: nameOf, colour: colourOf, narrationText, personOf, pov, povRange, shift, shiftInfo, narrator, reliability, limits, validate,
    tagsOf, isPd, caseOptions, caseWhyNot, caseTapWhy, caseInstance, povOptions, povWhyNot, askAnswer, povInstance,
    drawCases, drawPov, attempt, finish, castName,
    PROMPTS: Object.freeze({ trust: TRUST_PROMPT, tapUnreliable: TAP_UNRELIABLE, tapReliable: TAP_RELIABLE, name: NAME_PROMPT, nameShift: NAME_PROMPT_SHIFT, shift: SHIFT_PROMPT }),
  };
  root.Narration = Narration;
  const Bk = B();
  if (Bk && typeof Bk.register === 'function') Bk.register('narration', Narration);
  if (typeof module !== 'undefined' && module.exports) module.exports = Narration;
})(typeof window !== 'undefined' ? window : globalThis);
